import { gql } from '@apollo/client';
import Head from 'next/head';
import type { FC } from 'react';
import { memo } from 'react';
import type { Comment, Person, WithContext } from 'schema-dts';

import { useAppApolloClient } from '../../hooks/useAppApolloClient';
import { useWebsite } from '../../hooks/useWebsite';
import type { Guest, Message, User } from '../../types/api';
import { stripHtml } from '../../utils/dom';

interface Props {
  message: Message;
  topicId: string;
  isBestAnswer?: boolean;
}

export const MessageLd: FC<Props> = memo(function MessageLd(/* props */) {
  // Disable the structured data for messages, as decided with David, to avoid adding too much SEO data.
  return null;

  // return <MessageLdInner {...props} />;
});

export const MessageLdInner: FC<Props> = memo(function MessageLdInner(props) {
  const { message, topicId, isBestAnswer } = props;
  const client = useAppApolloClient();
  const { url, name } = useWebsite();
  const topic = client.readFragment({
    fragment: gql`
      fragment MessageLdTopic on Topic {
        id
        slug
        subject
      }
    `,
    id: `Topic:${topicId}`,
  });

  const safeContent = stripHtml(message.content ?? '');

  const authorPicture =
    (message?.author as User)?.profile?.picture ?? (message?.author as Guest)?.picture;
  const authorName = (message?.author as User)?.profile?.pseudo ?? message?.author?.username;
  const author: Person = {
    '@type': 'Person',
    name: authorName,
    image: authorPicture,
  };

  const isAnswer = false && /[?]/.test(topic?.subject);

  const votes = message?.votes ?? [];

  const topicUrl = `${url}/t/${topic?.slug}`.replace(/[^:]\/\//, '/');

  const jsonld: WithContext<Comment> = {
    '@context': 'https://schema.org',
    '@type': isAnswer ? 'Answer' : 'Comment',
    author,
    url: `${url}/t/${topic?.slug}/d/${message.id}`.replace(/[^:]\/\//, '/'),
    text: safeContent,
    dateCreated: message?.createdAt,
    datePublished: message?.createdAt,
    dateModified: message?.contentEditedAt ?? message?.createdAt,
    ...(message?.parentMessage?.id
      ? {
          parentItem: {
            '@type': 'Comment',
            '@id': `${url}/t/${topic?.slug}/d/${message?.parentMessage?.id}`.replace(
              /[^:]\/\//,
              '/',
            ),
          },
        }
      : null),
    discussionUrl: topicUrl,
    publisher: {
      '@type': 'Organization',
      legalName: name,
      name,
    },
    ...(isAnswer
      ? {
          upVoteCount: Math.max(
            votes.filter(elt => Number(elt?.score ?? 0) > 0).length -
              votes.filter(elt => Number(elt?.score ?? 0) < 0).length,
            0,
          ),
          '@reverse': {
            [isBestAnswer ? 'acceptedAnswer' : 'suggestedAnswer']: {
              '@type': 'Question',
              '@id': topicUrl,
            },
          },
        }
      : null),
  };

  return (
    <Head>
      <script
        key={`json-lds-message-${message?.id}`}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonld) }}
      />
    </Head>
  );
});
